import { toJS } from 'mobx';
import querystring from 'query-string';
import React from 'react';

import {
  ForwardRouteType,
  RouteType,
  ParamValuesType,
  RouteParamsType,
  RouteQueryParamsType,
} from 'types/RouteType';
import RootStore from '../RootStore';
import { DialogType } from 'types/DialogType';
import { ViewportCardType } from 'types/ViewportCardType';

export const getRegexMatches = (
  string: string,
  regexExpression: RegExp,
  callback: (match: RegExpExecArray) => void
): void => {
  let match: RegExpExecArray | null;
  while ((match = regexExpression.exec(string)) !== null) {
    callback(match);
  }
};
export const paramRegex = /(:([^/?]*)\??)/g;
export const optionalRegex = /(\/:[^/]*)?$/g;

class Route {
  key: string;
  path: string;
  component:
    | React.ComponentType<unknown>
    | React.LazyExoticComponent<React.ComponentType<unknown>>
    | null = null;
  componentPath: string | undefined;
  forwardRoute: string | ForwardRouteType | null = null;
  dialogs: DialogType[];
  beforeExit:
    | ((context: {
        route: RouteType;
        params: ParamValuesType;
        rootStore: RootStore;
        queryParams: ParamValuesType;
        nextPath: string;
        manualTrigger: boolean;
      }) => void)
    | null
    | undefined;
  onExit:
    | ((context: {
        route: RouteType;
        params: ParamValuesType;
        rootStore: RootStore;
        queryParams: ParamValuesType;
        nextPath: string;
        manualTrigger: boolean;
      }) => void)
    | null
    | undefined;
  beforeEnter:
    | ((context: {
        route: RouteType;
        params: ParamValuesType;
        rootStore: RootStore;
        queryParams: ParamValuesType;
        nextPath: string;
        manualTrigger: boolean;
      }) => void)
    | null
    | undefined;
  onEnter:
    | ((context: {
        route: RouteType;
        params: ParamValuesType;
        rootStore: RootStore;
        queryParams: ParamValuesType;
        nextPath: string;
        manualTrigger: boolean;
      }) => void)
    | null
    | undefined;
  onParamsChange:
    | ((context: {
        route: RouteType;
        params: ParamValuesType;
        rootStore: RootStore;
        queryParams: ParamValuesType;
        nextPath: string;
        manualTrigger: boolean;
      }) => void)
    | null
    | undefined;
  title: string;
  originalPath: string;
  subscription: unknown;
  subRoutes: RouteType[] | null;
  defaultParams: RouteParamsType | null;
  defaultQueryParams: RouteQueryParamsType | null;
  requiresAuth: boolean;
  requiresAdmin?: boolean;
  requiresPermission: boolean;
  projectRoute: boolean;
  parentRoute: string | null;
  permissionPath: string;
  channels: string[];
  enableAutoFind: boolean;
  rootPath: string;
  layoutPath: string;
  viewportCard: ViewportCardType | null = null;
  /**
   * Whether this route can function when offline.
   * Defaults to true - routes work offline by default (hooks are skipped).
   * Set to false for routes that require network connectivity.
   */
  offlineCapable: boolean = true;
  /**
   * Hook called when the app reconnects to the network while on this route.
   */
  onReconnect:
    | ((context: {
        route: RouteType;
        params: ParamValuesType;
        rootStore: RootStore;
        queryParams: ParamValuesType;
        nextPath: string;
        manualTrigger: boolean;
      }) => void | Promise<void>)
    | null
    | undefined;

  constructor(route: RouteType) {
    const {
      name = '',
      path,
      component,
      componentPath,
      onEnter,
      beforeExit,
      onParamsChange,
      beforeEnter,
      onExit,
      title,
      subRoutes,
      defaultParams,
      requiresAdmin,
      requiresAuth,
      requiresPermission,
      forwardRoute,
      defaultQueryParams,
      layout,
      subscription,
      projectRoute,
      parentRoute,
      permissionPath,
      channels = [],
      enableAutoFind,
      dialogs,
      viewportCard,
      offlineCapable,
      onReconnect,
    } = route;
    // console.log('layout:', layout);
    this.key = name.replace(/ /g, '');
    this.layoutPath = layout;
    this.path = layout + path;
    // @ts-expect-error - component is a function
    this.component = component;
    this.componentPath = componentPath;
    this.forwardRoute = forwardRoute ?? null;
    this.dialogs = dialogs || [];
    if (beforeExit) {
      this.beforeExit = beforeExit;
    }
    if (onExit) {
      this.onExit = onExit;
    }
    if (beforeEnter) {
      this.beforeEnter = beforeEnter;
    }
    if (onEnter) {
      this.onEnter = onEnter;
    }
    if (onParamsChange) {
      this.onParamsChange = onParamsChange;
    }

    this.title = title || name;
    this.originalPath = this.path;
    this.subscription = subscription || null;
    this.subRoutes = subRoutes && subRoutes.length ? subRoutes : null;
    this.defaultParams = defaultParams || null;
    this.defaultQueryParams = defaultQueryParams || null;
    this.requiresAuth = requiresAuth === false ? false : true;
    this.requiresAdmin = requiresAdmin || false;
    this.requiresPermission = requiresPermission === false ? false : true;
    this.projectRoute = projectRoute || false;
    this.parentRoute = parentRoute || null;
    this.permissionPath = permissionPath || layout + path || '';
    this.channels = [this.path.slice(1), ...channels];
    this.enableAutoFind = enableAutoFind || false;
    this.viewportCard = viewportCard || null;
    this.offlineCapable = offlineCapable !== false;
    if (onReconnect) {
      this.onReconnect = onReconnect;
    }
    //if there are optional parameters, replace the path with a regex expression
    this.path =
      this.path.indexOf('?') === -1
        ? this.path
        : this.path.replace(optionalRegex, '(/([^/]*)?)?$');
    // console.log(`this.path for Route - ${this.title}:`, this.path);
    this.rootPath = this.getRootPath();
    // console.log(`this.rootPath for Route - ${this.title}:`, this.rootPath);
    //bind
    this.getRootPath = this.getRootPath.bind(this);
    this.replaceUrlParams = this.replaceUrlParams.bind(this);
    this.getParamsObject = this.getParamsObject.bind(this);
  }
  /*
   Sets the root path for the current path, so it's easier to determine if the route entered/exited or just some params changed
   Example: for '/' the root path is '/', for '/profile/:username/:tab' the root path is '/profile'
   */
  getRootPath() {
    // console.log('this.path:', this.path);
    const rootPath = `/${this.path.split('/')[1]}`;
    return rootPath !== '/#' ? rootPath : `/${this.path.split('/')[2]}`;
  }

  /*
   replaces url params placeholders with params from an object
   Example: if url is /book/:id/page/:pageId and object is {id:100, pageId:200} it will return /book/100/page/200
   */
  replaceUrlParams(
    params: ParamValuesType,
    queryParams: ParamValuesType,
    pathOverride?: string
  ): string {
    let jsParams: Record<string, unknown> = params && toJS(params);
    if ((!params || Object.keys(params).length === 0) && this.defaultParams) {
      jsParams = this.defaultParams;
    }

    if (
      (!queryParams || Object.keys(queryParams).length === 0) &&
      this.defaultQueryParams &&
      !pathOverride
    ) {
      queryParams = this.defaultQueryParams as ParamValuesType;
    }
    if (!jsParams) {
      jsParams = {};
    }
    if (!queryParams) {
      queryParams = {};
    }

    if (Array.isArray(params)) jsParams = this.getParamsObject(params);

    const jsQueryParams: object = toJS(queryParams);
    const queryParamsString: string = querystring
      .stringify(jsQueryParams)
      .toString();
    const hasQueryParams: boolean = queryParamsString !== '';
    let newPath: string = pathOverride || this.originalPath;
    getRegexMatches(
      this.originalPath,
      paramRegex,
      // eslint-disable-next-line
      ([_fullMatch, paramKey, paramKeyWithoutColon]: RegExpExecArray) => {
        const value =
          Array.isArray(jsParams) && jsParams.length
            ? jsParams[0]
            : jsParams[paramKeyWithoutColon]
              ? jsParams[paramKeyWithoutColon]
              : undefined;
        newPath =
          value !== undefined
            ? newPath.replace(paramKey, value.toString())
            : newPath.replace(`/${paramKey}`, '');
      }
    );
    return `${newPath}${
      hasQueryParams ? `?${queryParamsString}` : ''
    }`.toString();
  }

  getParamsObject(paramValues: (string | number)[]): ParamValuesType {
    const params: string[] = [];
    getRegexMatches(
      this.originalPath,
      paramRegex,
      ([, paramKeyWithoutColon]: RegExpExecArray) => {
        params.push(paramKeyWithoutColon);
      }
    );
    const result: ParamValuesType = paramValues.reduce(
      (obj: ParamValuesType, paramValue, index) => {
        obj[params[index]] = paramValue.toString();
        return obj;
      },
      {} as ParamValuesType
    );
    return result;
  }
}

export default (route: RouteType): Route => new Route(route);
